import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * Default image behavior:
 * - Initial image = SECOND image
 * - Hover = FIRST image
 * - Mouse leave = SECOND image
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink
 * @property {import('slideshow').Slideshow} [slideshow]
 * @property {import('quick-add').QuickAddComponent} [quickAdd]
 * @property {HTMLElement} [cardGallery]
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null}
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null}
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link.
   *
   * @param {Event} event
   * @param {URL} url
   */
  #navigateToURL = (event, url) => {
    const shouldOpenInNewTab =
      event instanceof MouseEvent &&
      (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    }

    window.location.href = url.href;
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;

    if (!(link instanceof HTMLAnchorElement)) {
      throw new Error('Product card link not found');
    }

    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    /*
     * IMPORTANT:
     * Product card starts with SECOND image.
     *
     * We wait one frame so the slideshow is fully initialized.
     */
    requestAnimationFrame(() => {
      this.#setSecondImage();

      /*
       * Preload the first image because it is shown on hover.
       */
      this.#preloadFirstPreviewImage();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('click', this.navigateToProduct);
    this.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);
  }

  /**
   * Select SECOND image.
   *
   * This is the default image when:
   * - Collection page opens
   * - Product cards render
   * - Mouse leaves the card
   */
  #setSecondImage() {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const slides = slideshow.slides || [];

    if (slides.length < 2) return;

    /*
     * Remove variant-image hidden state so the second
     * normal product image can be displayed.
     */
    for (const slide of slides) {
      if (slide.getAttribute('variant-image') != null) {
        slide.hidden = false;
      }
    }

    /*
     * Slide index is zero-based:
     * 0 = FIRST image
     * 1 = SECOND image
     */
    slideshow.select(1, undefined, { animate: false });

    this.#previousSlideIndex = null;
  }

  /**
   * Select FIRST image.
   *
   * This is used while hovering over the product card.
   */
  #setFirstImage() {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const slides = slideshow.slides || [];

    if (slides.length === 0) return;

    /*
     * Slide index 0 = FIRST image
     */
    slideshow.select(0, undefined, { animate: false });
  }

  /**
   * Preload the first image because it is used on hover.
   */
  #preloadFirstPreviewImage() {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const firstSlide = slideshow.slides?.[0];

    firstSlide
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   *
   * @param {VariantSelectedEvent} event
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   *
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    event.stopPropagation();

    this.updatePrice(event);

    this.#isUnavailableVariantSelected(event);

    this.#updateProductUrl(event);

    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();

    this.#previousSlideIndex = null;

    this.removeAttribute('data-no-swatch-selected');

    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate.
   */
  #updateOverflowList() {
    const overflowList = this.querySelector(
      'swatches-variant-picker-component overflow-list'
    );

    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]')
      ? true
      : false;

    if (!overflowList || !isActiveOverflowList) return;

    requestAnimationFrame(() => {
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates product price.
   *
   * @param {VariantUpdateEvent} event
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(
      `product-price [ref='priceContainer']`
    )[1];

    const newPriceElement =
      event.detail.data.html.querySelector(
        `product-price [ref='priceContainer']`
      );

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates product URL.
   *
   * @param {VariantUpdateEvent} event
   */
  #updateProductUrl(event) {
    const anchorElement =
      event.detail.data.html?.querySelector('product-card a');

    const featuredMediaUrl =
      event.detail.data.html
        ?.querySelector('product-card-link')
        ?.getAttribute('data-featured-media-url');

    if (featuredMediaUrl && this.closest('product-card-link')) {
      this.closest('product-card-link')?.setAttribute(
        'data-featured-media-url',
        featuredMediaUrl
      );
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      if (anchorElement.getAttribute('href')?.trim() === '') {
        return;
      }

      const productUrl = anchorElement.href;

      const {
        productCardLink,
        productTitleLink,
        cardGalleryLink,
      } = this.refs;

      productCardLink.href = productUrl;

      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }

      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks whether an unavailable variant is selected.
   *
   * @param {VariantUpdateEvent} event
   */
  #isUnavailableVariantSelected(event) {
    const allVariants =
      /** @type {NodeListOf<HTMLInputElement>} */ (
        event.detail.data.html.querySelectorAll('input:checked')
      );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(
        variant.dataset.optionAvailable === 'true'
      );
    }
  }

  /**
   * Toggles add-to-cart button.
   *
   * @param {boolean} enable
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector(
      '.add-to-cart__button button'
    );

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide/show variant images.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;

    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId =
      this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden =
          slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select(
        { id: selectedImageId },
        undefined,
        { animate: false }
      );
    }
  }

  /**
   * Gets all variant inputs.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets variant picker.
   */
  get variantPicker() {
    return this.querySelector(
      'swatches-variant-picker-component'
    );
  }

  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles slideshow select event.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Preview variant.
   *
   * @param {string} id
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    slideshow.select(
      { id },
      undefined,
      { animate: false }
    );
  }

  /**
   * ============================================================
   * HOVER IMAGE
   * ============================================================
   *
   * BEFORE:
   * First image -> hover -> Second image
   *
   * NOW:
   * Second image -> hover -> First image
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    const slides = slideshow.slides || [];

    if (slides.length < 2) return;

    /*
     * On hover:
     * SHOW FIRST IMAGE
     */
    this.#setFirstImage();

    /*
     * Preload second image for mouse leave.
     */
    setTimeout(() => {
      this.#preloadSecondPreviewImage();
    });
  }

  /**
   * Preload second image.
   */
  #preloadSecondPreviewImage() {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const secondSlide = slideshow.slides?.[1];

    secondSlide
      ?.querySelector('img[loading="lazy"]')
      ?.removeAttribute('loading');
  }

  /**
   * ============================================================
   * MOUSE LEAVE
   * ============================================================
   *
   * Always return to SECOND image.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    this.resetVariant.cancel();

    /*
     * Mouse leave:
     * SHOW SECOND IMAGE
     */
    this.#setSecondImage();
  }

  /**
   * Resets the image to SECOND image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    const slides = slideshow.slides || [];

    /*
     * If product has at least two images,
     * always return to SECOND image.
     */
    if (slides.length >= 2) {
      slideshow.select(
        1,
        undefined,
        { animate: false }
      );

      this.#previousSlideIndex = null;

      return;
    }

    /*
     * If only one image exists, use first image.
     */
    if (slides.length === 1) {
      slideshow.select(
        0,
        undefined,
        { animate: false }
      );

      this.#previousSlideIndex = null;
    }
  };

  /**
   * Intercepts click event on product card.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    /*
     * Don't navigate in theme editor / no-navigation cards.
     */
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest(
      'button, input, label, select, [tabindex="1"]'
    );

    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;

    if (!link.href) return;

    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');

    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest(
      'results-list[infinite-scroll="true"]'
    );

    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);

      const parent = this.closest('li');

      url.hash = productCardAnchor;

      if (parent && parent.dataset.page) {
        url.searchParams.set(
          'page',
          parent.dataset.page
        );
      }

      yieldToMainThread().then(() => {
        history.replaceState(
          {},
          '',
          url.toString()
        );
      });
    }

    const targetLink = event.target.closest('a');

    /*
     * Let native navigation handle real links.
     */
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets variant.
   */
  resetVariant = debounce(
    this.#resetVariant,
    100
  );
}

if (!customElements.get('product-card')) {
  customElements.define(
    'product-card',
    ProductCard
  );
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {
 *   overflowList: HTMLElement
 * }} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    /*
     * Cache parent product card.
     */
    this.parentProductCard =
      this.closest('product-card');

    /*
     * Listen for variant updates to apply pending URL changes.
     */
    this.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleCardVariantUrlUpdate.bind(this)
    );
  }

  /**
   * Updates card URL when variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (
      this.pendingVariantId &&
      this.parentProductCard instanceof ProductCard
    ) {
      const currentUrl = new URL(
        this.parentProductCard.refs.productCardLink.href
      );

      currentUrl.searchParams.set(
        'variant',
        this.pendingVariantId
      );

      this.parentProductCard.refs.productCardLink.href =
        currentUrl.toString();

      this.pendingVariantId = null;
    }
  }

  /**
   * Override variantChanged method.
   *
   * Handles unavailable swatches with available alternatives.
   *
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    /*
     * Check whether this is a swatch input.
     */
    const isSwatchInput =
      event.target instanceof HTMLInputElement &&
      event.target.name?.includes('-swatch');

    const clickedSwatch = event.target;

    const availableCount = parseInt(
      clickedSwatch.dataset.availableCount || '0'
    );

    const firstAvailableVariantId =
      clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    /*
     * Special unavailable variant handling.
     */
    if (
      isSwatchInput &&
      availableCount > 0 &&
      firstAvailableVariantId
    ) {
      event.stopPropagation();

      /*
       * Update selected swatch visually.
       */
      this.updateSelectedOption(
        clickedSwatch
      );

      /*
       * Build product URL.
       */
      const productUrl =
        this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(
        productUrl,
        window.location.origin
      );

      url.searchParams.set(
        'variant',
        firstAvailableVariantId
      );

      url.searchParams.set(
        'section_id',
        'section-rendering-product-card'
      );

      const requestUrl = url.href;

      /*
       * Store pending variant.
       */
      this.pendingVariantId =
        firstAvailableVariantId;

      /*
       * Fetch updated product card.
       */
      this.fetchUpdatedSection(
        requestUrl
      );

      return;
    }

    /*
     * Default variant handling.
     */
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   *
   * @param {Event} [event]
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (
      overflowList instanceof OverflowList
    ) {
      overflowList.showAll();
    }
  }
}

if (
  !customElements.get(
    'swatches-variant-picker-component'
  )
) {
  customElements.define(
    'swatches-variant-picker-component',
    SwatchesVariantPickerComponent
  );
}